import React from 'react';
import { Switch, Typography, Tooltip, Tag } from 'antd';
import { SafetyCertificateOutlined, CrownOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import type { RbacRole } from '../../services/rbac/rbacService';

const { Text } = Typography;

interface RoleCardProps {
  role: RbacRole;
  selected: boolean;
  onToggle: (roleId: string, checked: boolean) => void;
  disabled?: boolean;
  showPermissionsPreview?: boolean;
}

const ROLE_COLORS: Record<string, string> = {
  admin: '#434343',
  manager: '#595959',
  user: '#8c8c8c',
  default: '#bfbfbf',
};

const ROLE_ICONS: Record<string, React.ReactNode> = {
  admin: <CrownOutlined />,
  manager: <TeamOutlined />,
  user: <UserOutlined />,
  default: <SafetyCertificateOutlined />,
};

export const RoleCard: React.FC<RoleCardProps> = ({
  role,
  selected,
  onToggle,
  disabled = false,
  showPermissionsPreview = true,
}) => {
  const color = ROLE_COLORS[role.value] || ROLE_COLORS.default;
  const icon = ROLE_ICONS[role.value] || ROLE_ICONS.default;
  const permissionsCount = role.permissions?.length || 0;

  const permissionsByCategory = React.useMemo(() => {
    if (!role.permissions || !showPermissionsPreview) return {};

    const categories: Record<string, number> = {};
    role.permissions.forEach(perm => {
      const resourceValue = perm.resource.value;
      let category = 'other';
      
      if (resourceValue.startsWith('sales')) category = 'sales';
      else if (resourceValue.startsWith('purchase')) category = 'purchase';
      else if (resourceValue === 'references') category = 'references';
      else if (resourceValue === 'admin') category = 'admin';

      categories[category] = (categories[category] || 0) + 1;
    });

    return categories;
  }, [role.permissions, showPermissionsPreview]);

  const categoryNames: Record<string, string> = {
    sales: 'Продажи',
    purchase: 'Закупки',
    references: 'Управление',
    admin: 'Админка',
    other: 'Прочее',
  };

  return (
    <div
      className={`role-card-compact ${selected ? 'role-card-compact--selected' : ''}`}
      style={{
        border: `1px solid ${selected ? color : 'var(--color-border-primary)'}`,
        borderRadius: '6px',
        padding: '12px',
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.2s ease',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: selected ? 'var(--color-bg-primary)' : 'var(--color-bg-surface)',
      }}
      onClick={() => !disabled && onToggle(role.id, !selected)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ fontSize: '16px', color }}>{icon}</div>
            <Text strong style={{ fontSize: '13px', color: 'var(--color-text-primary)' }}>
              {role.name}
            </Text>
            {role.isDefault && (
              <Tag bordered={false} style={{ margin: 0, fontSize: '10px', padding: '0 6px', lineHeight: '18px', background: 'var(--color-bg-primary)', color: 'var(--color-text-disabled)' }}>
                по умолчанию
              </Tag>
            )}
          </div>
        </div>
        <Switch
          size="small"
          checked={selected}
          disabled={disabled}
          onClick={(_, e) => e.stopPropagation()}
          onChange={(checked) => onToggle(role.id, checked)}
        />
      </div>

      {role.description && (
        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: '8px', lineHeight: '1.4' }}>
          {role.description}
        </Text>
      )}

      {showPermissionsPreview && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            {permissionsCount} {permissionsCount === 1 ? 'право' : permissionsCount > 4 ? 'прав' : 'права'}
          </Text>
          {Object.keys(permissionsByCategory).length > 0 && (
            <>
              <span style={{ color: 'var(--color-border-primary)' }}>•</span>
              {Object.entries(permissionsByCategory).slice(0, 2).map(([category, count]) => (
                <Tag
                  key={category}
                  bordered={false}
                  style={{
                    margin: 0,
                    fontSize: '10px',
                    padding: '0 6px',
                    lineHeight: '18px',
                    background: 'var(--color-bg-primary)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  {categoryNames[category]}: {count}
                </Tag>
              ))}
              {Object.keys(permissionsByCategory).length > 2 && (
                <Tooltip title={
                  <div>
                    {Object.entries(permissionsByCategory).slice(2).map(([cat, count]) => (
                      <div key={cat} style={{ fontSize: '11px' }}>
                        {categoryNames[cat]}: {count}
                      </div>
                    ))}
                  </div>
                }>
                  <Tag bordered={false} style={{ margin: 0, fontSize: '10px', padding: '0 6px', lineHeight: '18px', background: 'var(--color-bg-primary)', color: 'var(--color-text-disabled)' }}>
                    +{Object.keys(permissionsByCategory).length - 2}
                  </Tag>
                </Tooltip>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};


